import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SportsEsports,
  Star,
  Lock,
  ThumbUp,
  EmojiEvents,
  Settings,
  Timeline,
  TextFields,
} from "@mui/icons-material";
import { Rules as RulesType } from "../../features/cfbd/types/rules";

interface RulesProps {
  rules: RulesType;
}

const Rules = ({ rules }: RulesProps) => {
  const theme = useTheme();

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
          color:
            theme.palette.mode === "dark"
              ? "#1C6B50"
              : theme.palette.text.primary,
        }}
      >
        <Settings
          sx={{
            color:
              theme.palette.mode === "dark"
                ? "#1C6B50"
                : theme.palette.primary.main,
          }}
        />
        League Rules
      </Typography>

      <Stack spacing={3}>
        {/* Basic Game Rules */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <SportsEsports color="primary" />
              Basic Game Rules
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={`${rules.gameCount} Games`}
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body1">
                  Number of games per week
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={`+${rules.gameScoring.bonus}`}
                  color="success"
                  variant="outlined"
                  icon={<ThumbUp />}
                />
                <Typography variant="body1">
                  Points for correct picks
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={`${rules.gameScoring.penalty}`}
                  color="error"
                  variant="outlined"
                />
                <Typography variant="body1">
                  Points for incorrect picks
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Game of the Week */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Star color="warning" />
              Game of the Week
            </Typography>
            {rules.gameOfTheWeekRules ? (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`+${rules.gameOfTheWeekRules.bonus}`}
                    color="warning"
                    variant="outlined"
                    icon={<EmojiEvents />}
                  />
                  <Typography variant="body1">
                    Bonus points for correct Game of the Week pick
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${rules.gameOfTheWeekRules.penalty}`}
                    color="error"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Penalty points for incorrect Game of the Week pick
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Game of the Week is disabled
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Locks */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Lock color="secondary" />
              Locks
            </Typography>
            {rules.lockRules ? (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${rules.lockRules.count} Locks`}
                    color="secondary"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Number of locks per week
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={rules.lockRules.uniqueLock ? "Unique" : "Not Unique"}
                    color={rules.lockRules.uniqueLock ? "success" : "default"}
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Locks{" "}
                    {rules.lockRules.uniqueLock
                      ? "must be"
                      : "do not need to be"}{" "}
                    unique
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`+${rules.lockRules.scoring.bonus}`}
                    color="success"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Points for correct lock picks
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${rules.lockRules.scoring.penalty}`}
                    color="error"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Points for incorrect lock picks
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Locks are disabled
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Nails */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <ThumbUp color="info" />
              Nails
            </Typography>
            {rules.nailRules ? (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={
                      rules.nailRules.trackNailsSeparately
                        ? "Separate"
                        : "Combined"
                    }
                    color="info"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Nails{" "}
                    {rules.nailRules.trackNailsSeparately ? "are" : "are not"}{" "}
                    tracked separately
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`+${rules.nailRules.scoring.bonus}`}
                    color="success"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Points for correct nail picks
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={`${rules.nailRules.scoring.penalty}`}
                    color="error"
                    variant="outlined"
                  />
                  <Typography variant="body1">
                    Points for incorrect nail picks
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Nails are disabled
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Tiebreakers */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <EmojiEvents color="warning" />
              Tiebreakers
            </Typography>
            {rules.tiebreakerRules ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={
                    rules.tiebreakerRules.defaultToGotw
                      ? "Default to GOTW"
                      : "No Default"
                  }
                  color="warning"
                  variant="outlined"
                />
                <Typography variant="body1">
                  Tiebreakers{" "}
                  {rules.tiebreakerRules.defaultToGotw
                    ? "default"
                    : "do not default"}{" "}
                  to Game of the Week
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Tiebreakers are disabled
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Pushes */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Settings color="primary" />
              Pushes
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Chip
                label={rules.pushEnabled ? "Enabled" : "Disabled"}
                color={rules.pushEnabled ? "success" : "default"}
                variant="outlined"
              />
              <Typography variant="body1">
                Pushes are {rules.pushEnabled ? "enabled" : "disabled"}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Lines (Official vs Custom) */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <Timeline color="success" />
              Game Lines
            </Typography>
            {rules.flavorLinesRules ? (
              <Stack spacing={2}>
                {/* Custom Lines */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={
                      rules.flavorLinesRules.customGameLinesEnabled
                        ? "On"
                        : "Off"
                    }
                    color={
                      rules.flavorLinesRules.customGameLinesEnabled
                        ? "success"
                        : "default"
                    }
                    variant="outlined"
                  />
                  <Typography variant="body1">Custom Lines</Typography>
                </Box>
                {/* Hide Official Lines toggle (only relevant when custom is enabled) */}
                {rules.flavorLinesRules.customGameLinesEnabled && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Chip
                      label={
                        rules.flavorLinesRules
                          .hideOfficialLinesWhenCustomEnabled
                          ? "On"
                          : "Off"
                      }
                      color={
                        rules.flavorLinesRules
                          .hideOfficialLinesWhenCustomEnabled
                          ? "success"
                          : "default"
                      }
                      variant="outlined"
                    />
                    <Typography variant="body1">Hide Official Lines</Typography>
                  </Box>
                )}
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Lines are disabled
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Season Winner */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <EmojiEvents color="success" />
              Season Winner
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Chip
                  label={
                    rules.seasonWinnerRules?.orderBy === "weeklyWins"
                      ? "Weekly Wins"
                      : "Total Points"
                  }
                  color="success"
                  variant="outlined"
                />
                <Typography variant="body1">
                  Season winner determined by{" "}
                  {rules.seasonWinnerRules?.orderBy === "weeklyWins"
                    ? "weekly wins"
                    : "total points"}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Flavor Text */}
        <Card elevation={2}>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <TextFields color="success" />
              Flavor Text
            </Typography>
            {rules.flavorLinesRules ? (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Chip
                    label={
                      rules.flavorLinesRules.flavorTextEnabled ? "On" : "Off"
                    }
                    color={
                      rules.flavorLinesRules.flavorTextEnabled
                        ? "success"
                        : "default"
                    }
                    variant="outlined"
                  />
                  <Typography variant="body1">Text for Games</Typography>
                </Box>
              </Stack>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Flavor Text is disabled
              </Typography>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
};

export default Rules;
